/**
 * Rutas desconocidas y errores de render.
 *
 * El catch-all de Django sirve la app para cualquier ruta, así que sin una
 * ruta comodín en el router una dirección mal escrita dejaba la página en
 * blanco. Y sin un error boundary, una excepción al renderizar hacía lo mismo:
 * pantalla vacía y ni una pista de qué pasó.
 */

import { AlertTriangle, ArrowLeft, Compass, RotateCcw } from 'lucide-react'
import { Component, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Boton, Panel } from '../ui/primitivos'

export function NoEncontrado() {
  const ubicacion = useLocation()

  return (
    <div className="mx-auto max-w-2xl p-6">
      <Panel titulo="Página no encontrada">
        <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
          <Compass size={24} className="text-[var(--color-texto-tenue)]" />
          <p className="text-[14px] font-medium">Esta dirección no existe</p>
          <p className="max-w-md text-[13px] text-[var(--color-texto-suave)]">
            No hay nada en{' '}
            <code className="rounded bg-[var(--color-acento-suave)] px-1">
              {ubicacion.pathname}
            </code>
            . Puede que el enlace esté mal, o que la entidad se haya borrado.
          </p>
          <Link to="/" className="mt-2">
            <Boton variante="primario" icono={<ArrowLeft size={14} />}>
              Volver al inicio
            </Boton>
          </Link>
        </div>
      </Panel>
    </div>
  )
}

interface EstadoBoundary {
  error: Error | null
}

/**
 * Última red de seguridad ante una excepción de render.
 *
 * Se implementa como clase porque React no ofrece equivalente en hooks: no
 * existe un `useErrorBoundary`.
 */
export class LimiteDeError extends Component<{ children: ReactNode }, EstadoBoundary> {
  state: EstadoBoundary = { error: null }

  static getDerivedStateFromError(error: Error): EstadoBoundary {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Queda en la consola para poder pegarlo en un reporte.
    console.error('Error de render en StakeMap:', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="mx-auto max-w-2xl p-6">
        <Panel titulo="La interfaz falló">
          <div className="p-4">
            <div className="flex items-start gap-2.5">
              <AlertTriangle
                size={18}
                className="mt-0.5 shrink-0 text-[var(--color-opositora)]"
              />
              <div className="min-w-0">
                <p className="text-[13px]">
                  Algo se rompió al dibujar esta vista. Los datos del servidor están intactos:
                  no se perdió nada de la investigación.
                </p>
                <pre className="scroll-sutil mt-3 max-h-40 overflow-auto rounded-md border border-[var(--color-borde)] bg-[var(--color-lienzo)] p-2.5 text-[11px] whitespace-pre-wrap">
                  {this.state.error.message}
                </pre>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Boton
                variante="primario"
                icono={<RotateCcw size={14} />}
                onClick={() => this.setState({ error: null })}
              >
                Volver a intentar
              </Boton>
              <Boton variante="secundario" onClick={() => window.location.assign('/')}>
                Ir al inicio
              </Boton>
            </div>
          </div>
        </Panel>
      </div>
    )
  }
}
